import { useState, useEffect } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Resolve a public path or absolute URL into something <img/> can render.
const resolveImg = (path) => {
  if (!path) return null;
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  // /category-icons/* live in /app/frontend/public, so leave as-is.
  return path;
};

/**
 * Welcome envelope intro.
 *
 * Branding (background color + center icon) is admin-controlled via the
 * `categories` collection and mirrored in `ui_assets.asset_cover_defaults.config`.
 *
 * Resolution chain:
 *   1. Explicit props (per-host override on the invite/portal record)
 *   2. Fetched canonical category branding via /api/categories/branding
 *   3. Soft black fallback (#0a0a0a + initials sticker)
 */
export default function EnvelopeAnimation({
  onComplete,
  // Per-invite/per-host overrides
  coverImage,
  backgroundImage,
  logoImage,
  iconUrl,           // host-specific icon override
  bgColor,           // host-specific dark hex override
  textColor = "#ffffff",
  inviteText = "YOU'RE INVITED",
  // Identifying info
  categoryId,
  pilotName,
  stickerInitials,
  recipientName,
}) {
  const [opened, setOpened] = useState(false);
  const [branding, setBranding] = useState(null);

  // Pull canonical category branding (icon + dark hex) once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!categoryId) return;
      try {
        const r = await fetch(`${API}/categories/branding`);
        if (!r.ok) return;
        const map = await r.json();
        if (!cancelled) setBranding(map[categoryId] || null);
      } catch (_) {
        /* offline-safe */
      }
    })();
    return () => { cancelled = true; };
  }, [categoryId]);

  // Auto-open after 3.5s
  useEffect(() => {
    const t = setTimeout(() => handleOpen(), 3500);
    return () => clearTimeout(t);
  }, []);

  const handleOpen = () => {
    setOpened(true);
    setTimeout(() => onComplete?.(), 800);
  };

  const getInitials = () => {
    if (stickerInitials) return stickerInitials;
    if (!pilotName) return "PH";
    const w = pilotName.split(/\s+/);
    if (w.length >= 2) return (w[0][0] + w[1][0]).toUpperCase();
    return pilotName.slice(0, 2).toUpperCase();
  };

  // Resolve background hex.
  // Per user direction (Apr 30 2026): default to LIGHT category hex so the
  // envelope and cover never fall back to the Phygital dark wordmark. The
  // host can still override via prop (bgColor) when they really want dark.
  const resolvedBg = bgColor || branding?.light_hex || "#f3f4f6";
  // Text color follows: when the bg is light (default), use the category
  // dark_hex for text contrast; when host overrode to a dark bg, keep white.
  const resolvedTextColor = bgColor ? textColor : (branding?.dark_hex || "#0a0a0a");
  const stickerRingHex = branding?.dark_hex || "#0a0a0a";

  // Resolve sticker icon: prop overrides → coverImage/logoImage (host) → category icon → initials
  const stickerIcon =
    resolveImg(iconUrl) ||
    resolveImg(coverImage) ||
    resolveImg(logoImage) ||
    resolveImg(branding?.icon_url) ||
    null;

  const bgImage = backgroundImage ? resolveImg(backgroundImage) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center font-sans overflow-hidden cursor-pointer px-4"
      style={{
        backgroundColor: resolvedBg,
        backgroundImage: bgImage ? `url(${bgImage})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      onClick={handleOpen}
      data-testid="envelope-animation"
    >
      <div
        className={`w-full max-w-sm mx-auto transition-all duration-1000 ease-out ${
          opened ? "scale-110 opacity-0" : "scale-100 opacity-100"
        }`}
      >
        {/* Editable invite text */}
        <div className="text-center mb-6 sm:mb-8 px-2">
          <p
            className="text-xl sm:text-2xl md:text-3xl font-bold tracking-[0.2em] sm:tracking-[0.3em] uppercase drop-shadow-lg break-words"
            style={{ color: resolvedTextColor }}
          >
            {inviteText}
          </p>
        </div>

        {/* Envelope card with center sticker */}
        <div className="relative flex justify-center">
          <div className="w-full max-w-xs sm:max-w-sm h-44 sm:h-52 bg-white rounded-2xl shadow-2xl relative overflow-hidden mx-auto">
            <div
              className="absolute top-0 left-0 right-0 h-14 sm:h-16 bg-gradient-to-b from-gray-100 to-white"
              style={{ clipPath: "polygon(0 0, 50% 100%, 100% 0)" }}
            />
            <div className="absolute inset-3 sm:inset-4 top-10 sm:top-12 bg-gray-50 rounded-xl border border-gray-200" />
          </div>

          {/* Center sticker - uses category icon + category dark hex ring */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
            <div
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center shadow-xl overflow-hidden"
              style={{
                backgroundColor: "#ffffff",
                boxShadow: `0 0 0 4px ${stickerRingHex}, 0 20px 25px -5px rgb(0 0 0 / 0.25)`,
              }}
            >
              {stickerIcon ? (
                <img
                  src={stickerIcon}
                  alt={pilotName || branding?.display_name || "Category"}
                  className="w-12 h-12 sm:w-14 sm:h-14 object-contain"
                  data-testid="envelope-sticker-icon"
                />
              ) : (
                <span
                  className="font-bold text-xl sm:text-2xl tracking-widest"
                  style={{ color: stickerRingHex }}
                  data-testid="envelope-sticker-initials"
                >
                  {getInitials()}
                </span>
              )}
            </div>
          </div>
        </div>

        <p
          className="text-center text-xs sm:text-sm mt-6 sm:mt-8 animate-pulse"
          style={{ color: resolvedTextColor, opacity: 0.65 }}
        >
          Tap to open
        </p>
      </div>
    </div>
  );
}
